import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaArrowRight, FaRocket, FaStar, FaCode, FaShieldAlt, FaLightbulb } from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import gsap from "gsap";

/* ── Brain Visualization ── */
const BrainViz = () => {
  const [active, setActive] = React.useState(false);
  const timerRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (active) return;
    timerRef.current = setTimeout(() => setActive(true), 2500);
  };
  const handleMouseLeave = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    setActive(false);
  };
  const handleClick = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    setActive(true);
  };

  return (
    <div
      className="relative w-full flex items-center justify-center select-none"
      style={{ minHeight: 0 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Hidden canvas used only for pixel hit-testing */}
      
      {/* Outer ring pulse */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: '92%', height: '92%',
          border: '1px solid rgba(65,168,255,0.18)',
          borderRadius: '50%',
        }}
        animate={{ scale: [1, 1.08, 1], opacity: [0.5, 0.15, 0.5] }}
        transition={{ duration: 4, ease: 'easeInOut', repeat: Infinity }}
      />
      {/* Middle ring pulse — offset phase */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: '75%', height: '75%',
          border: '1px solid rgba(100,200,255,0.22)',
          borderRadius: '50%',
        }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.08, 0.3] }}
        transition={{ duration: 4, ease: 'easeInOut', repeat: Infinity, delay: 1.3 }}
      />

      {/* Brain image — gentle float + subtle breathe scale */}
      <motion.div
        className="relative w-full"
        animate={{
          y: [0, -14, 0, 14, 0],
          scale: [1, 1.03, 1, 1.03, 1],
        }}
        transition={{ duration: 7, ease: 'easeInOut', repeat: Infinity }}
        onClick={handleClick}
        onTouchStart={() => setActive(v => !v)}
        style={{ cursor: 'pointer' }}
      >
        <img
          src="/Brain3.png"
          alt=""
          loading="eager"
          className="w-full h-auto"
          style={{
            filter: 'brightness(0.92) saturate(1.2)',
            transform: 'scale(1.12)',
          }}
        />
      </motion.div>

      {/* Hover / tap overlay */}
      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: active ? 1 : 0 }}
        transition={{ duration: 0.35 }}
        style={{
          background: 'linear-gradient(135deg, rgba(3,7,18,0.88) 0%, rgba(17,24,39,0.82) 100%)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(75,85,99,0.25)',
        }}
      >
        <motion.div
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: active ? 0 : 12, opacity: active ? 1 : 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="flex flex-col items-center gap-3 px-6 text-center"
        >
          {/* Icon container — matches stat icons in hero */}
          <div className="relative inline-block mb-1">
            <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-900 rounded-lg blur-lg opacity-30" />
            <div className="relative w-12 h-12 mx-auto bg-gray-900/70 rounded-lg border border-gray-700 flex items-center justify-center">
              <FaLightbulb className="text-gray-300 text-xl" />
            </div>
          </div>
          <p className="text-white font-semibold text-lg leading-snug" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Have a project in mind?
          </p>
          <p className="text-gray-400 text-sm">
            Let's build something great together.
          </p>
          <Link
            to="/contact"
            className="group pointer-events-auto relative mt-1 px-6 py-2.5 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 inline-flex items-center gap-2 border border-gray-700 hover:border-gray-600 text-sm"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="relative flex items-center gap-2">
              Contact Us
              <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </span>
          </Link>
        </motion.div>
      </motion.div>

      {/* Floating particle dots */}
      {[
        { top: '12%', left: '18%', delay: 0,   size: 5 },
        { top: '22%', left: '76%', delay: 1.2, size: 4 },
        { top: '68%', left: '82%', delay: 0.6, size: 6 },
        { top: '78%', left: '15%', delay: 1.8, size: 4 },
        { top: '45%', left: '6%',  delay: 0.9, size: 3 },
        { top: '50%', left: '92%', delay: 2.1, size: 3 },
      ].map((p, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            top: p.top, left: p.left,
            width: p.size, height: p.size,
            background: 'rgba(130,210,255,0.75)',
            boxShadow: '0 0 8px 2px rgba(65,168,255,0.6)',
          }}
          animate={{ y: [0, -10, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 3.5, ease: 'easeInOut', repeat: Infinity, delay: p.delay }}
        />
      ))}
    </div>
  );
};

const HeroSection = () => {
  const heroRef = useRef(null);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (heroRef.current) {
      const ctx = gsap.context(() => {
        // Grid lines animation
        gsap.to(".grid-line", {
          backgroundPosition: "100% 100%",
          duration: 30,
          repeat: -1,
          ease: "none",
        });

        // Floating particles animation
        gsap.to(".floating-particle", {
          y: "random(-30, 30)",
          x: "random(-30, 30)",
          rotation: "random(-180, 180)",
          duration: "random(4, 8)",
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
          stagger: {
            each: 0.3,
            from: "random",
          },
        });

        // Glowing orbs animation
        gsap.to(".glow-orb", {
          scale: 1.2,
          opacity: 0.6,
          duration: 3,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
      }, heroRef);

      return () => ctx.revert();
    }
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-transparent"
      style={{ paddingTop: '80px' }}
    >
      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={ref}>

          {/* Two-column: text left, brain right — stacks vertically on mobile */}
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-8 items-center">

            {/* Left — hero text */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              className="flex flex-col justify-center py-16 lg:py-20"
            >
              {/* Available for projects badge */}
              <motion.div variants={itemVariants} className="mb-6">
                <Link to="/contact" className="inline-flex items-center gap-2 text-sm hover:opacity-80 transition-opacity duration-200 cursor-pointer">
                  <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                  <span className="text-gray-300 font-medium">Available For New Projects</span>
                  <span className="text-gray-500 hidden sm:inline">· Responds within 24h</span>
                </Link>
              </motion.div>

              {/* Main Title */}
              <motion.div variants={itemVariants} className="mb-6">
                <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-7xl font-black tracking-tight">
                  <span className="text-white">Build</span>
                  <br />
                  <span className="relative">
                    <span className="relative z-10 bg-gradient-to-r from-gray-300 via-gray-100 to-gray-400 bg-clip-text text-transparent">
                      Extraordinary
                    </span>
                  </span>
                  <br />
                  <span className="text-white">Digital Solutions</span>
                </h1>
              </motion.div>

              {/* Description */}
              <motion.p
                variants={itemVariants}
                className="text-base sm:text-lg md:text-xl text-gray-400 max-w-xl mb-10 leading-relaxed"
              >
                We architect and build high-performance software solutions
                that drive innovation and deliver measurable business results.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                variants={itemVariants}
                className="flex flex-col sm:flex-row gap-4 justify-start"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/contact"
                    className="group relative px-8 py-4 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 inline-flex items-center gap-2 border border-gray-700 hover:border-gray-600"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <span className="relative flex items-center gap-2">
                      Start Your Journey
                      <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </Link>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/projects"
                    className="group px-8 py-4 bg-transparent text-gray-300 font-semibold rounded-xl border border-gray-800 hover:border-gray-700 hover:text-white transition-all duration-300 inline-flex items-center gap-2"
                  >
                    <FaCode className="group-hover:rotate-6 transition-transform duration-300" />
                    Explore Portfolio
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Brain Visualization — below text on mobile, right column on desktop */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 1, delay: 0.5 }}
              className="flex items-center justify-center w-full"
            >
              <div className="w-64 sm:w-72 lg:w-full max-w-xs sm:max-w-sm lg:max-w-none">
                <BrainViz />
              </div>
            </motion.div>

          </div>

          {/* Stats Row — full width below the grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-5xl mx-auto pb-16"
          >
            {[
              { value: "150+", label: "Projects Delivered", icon: FaRocket },
              { value: "99.5%", label: "Client Retention", icon: FaStar },
              { value: "24/7", label: "Premium Support", icon: FaShieldAlt },
              { value: "50+", label: "Team Experts", icon: FaCode },
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="text-center group"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="relative inline-block mb-2">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-900 rounded-lg blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300" />
                  <div className="relative w-12 h-12 mx-auto bg-gray-900/50 backdrop-blur-sm rounded-lg border border-gray-800 flex items-center justify-center">
                    <stat.icon className="text-gray-400 text-xl group-hover:text-gray-300 transition-colors duration-300" />
                  </div>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-xs tracking-wider text-gray-500 uppercase">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>

        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-950 to-transparent pointer-events-none" />
    </section>
  );
};

export default HeroSection;
